document.addEventListener('DOMContentLoaded', function () {
    'use strict';

    function easeInOut(t) {
        return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    }

    /**
     * Flexbox scroll carousel: native horizontal scroll + pointer-drag momentum,
     * snap-to-slide on release, optional button navigation + bullet pagination,
     * and an "image-nav" mode that vertically centers the nav buttons on the
     * first slide's image. Used by .grid-swiper (buttons/pagination/image-nav)
     * and .quote-swiper (drag-scroll only -- its markup has no buttons/pagination).
     */
    function initGridCarousel(el, opts = {}) {
        const { imageNav = false, speed = 700 } = opts;

        const wrapper = el.querySelector('.swiper-wrapper');
        if (!wrapper) return;

        const slides = [...wrapper.querySelectorAll('.swiper-slide')];
        if (!slides.length) return;

        const prevBtn = el.querySelector('.swiper-button-prev');
        const nextBtn = el.querySelector('.swiper-button-next');
        const paginationEl = el.querySelector('.swiper-pagination');

        // How many slides currently fit in one view -- derived from the actual
        // rendered slide width instead of a hardcoded breakpoint table, so it
        // can never drift out of sync with the CSS width reservations (see
        // swiper.css) the way a duplicated JS table could.
        function groupSize() {
            const w = slides[0].getBoundingClientRect().width;
            if (!w) return 1;
            return Math.max(1, Math.round(wrapper.clientWidth / w));
        }

        let bullets = [];
        function buildBullets() {
            if (!paginationEl) return;
            paginationEl.innerHTML = '';
            paginationEl.classList.add('swiper-pagination-bullets');
            const pages = Math.max(1, Math.ceil(slides.length / groupSize()));
            bullets = [];
            for (let i = 0; i < pages; i++) {
                const bullet = document.createElement('span');
                bullet.className = 'swiper-pagination-bullet';
                bullet.setAttribute('role', 'button');
                bullet.setAttribute('aria-label', `Gå till sida ${i + 1}`);
                bullet.addEventListener('click', () => go(i * groupSize()));
                paginationEl.appendChild(bullet);
                bullets.push(bullet);
            }
        }

        function updateBullets(index) {
            if (!bullets.length) return;
            const page = Math.min(bullets.length - 1, Math.floor(index / groupSize()));
            bullets.forEach((b, i) => b.classList.toggle('swiper-pagination-bullet-active', i === page));
        }

        // Matches real Swiper's own watchOverflow default: hide nav/pagination
        // entirely when every slide already fits without scrolling. `!important`
        // inline is required here -- these buttons carry Tailwind's own
        // `!hidden md:!flex` utilities, and a plain inline style loses to an
        // author-origin `!important` class regardless of source order.
        function updateControlsLock() {
            const overflowing = wrapper.scrollWidth > wrapper.clientWidth + 1;
            [prevBtn, nextBtn, paginationEl].forEach((elm) => {
                if (!elm) return;
                if (overflowing) {
                    elm.style.removeProperty('display');
                } else {
                    elm.style.setProperty('display', 'none', 'important');
                }
            });
        }

        function updateButtons() {
            const max = wrapper.scrollWidth - wrapper.clientWidth;
            const atStart = wrapper.scrollLeft <= 1;
            const atEnd = wrapper.scrollLeft >= max - 1;
            if (prevBtn) {
                prevBtn.classList.toggle('swiper-button-disabled', atStart);
                prevBtn.setAttribute('aria-disabled', atStart ? 'true' : 'false');
            }
            if (nextBtn) {
                nextBtn.classList.toggle('swiper-button-disabled', atEnd);
                nextBtn.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
            }
        }

        let current = 0;
        let animRaf = null;

        function slideLefts() {
            const wrapRect = wrapper.getBoundingClientRect();
            return slides.map((s) => s.getBoundingClientRect().left - wrapRect.left + wrapper.scrollLeft);
        }

        function animateTo(target, duration) {
            cancelAnimationFrame(animRaf);
            const startPos = wrapper.scrollLeft;
            const delta = target - startPos;
            if (Math.abs(delta) < 1 || duration <= 0) {
                wrapper.scrollLeft = target;
                return;
            }
            const startTime = performance.now();
            function tick(now) {
                const t = Math.min(1, (now - startTime) / duration);
                wrapper.scrollLeft = startPos + delta * easeInOut(t);
                if (t < 1) animRaf = requestAnimationFrame(tick);
            }
            animRaf = requestAnimationFrame(tick);
        }

        function go(idx) {
            const lefts = slideLefts();
            const clamped = Math.max(0, Math.min(idx, slides.length - 1));
            const max = wrapper.scrollWidth - wrapper.clientWidth;
            animateTo(Math.max(0, Math.min(lefts[clamped], max)), speed);
            current = clamped;
            updateBullets(current);
        }

        prevBtn?.addEventListener('click', () => {
            if (prevBtn.classList.contains('swiper-button-disabled')) return;
            go(current - groupSize());
        });
        nextBtn?.addEventListener('click', () => {
            if (nextBtn.classList.contains('swiper-button-disabled')) return;
            go(current + groupSize());
        });

        function settle() {
            // Pin to the first/last slide when scroll is clamped at either bound --
            // with a slide count that isn't a clean multiple of the group size, the
            // last page can only scroll partway there, so its scrollLeft can land
            // closer to an earlier slide's native offset than to the last slide's.
            const max = wrapper.scrollWidth - wrapper.clientWidth;
            if (wrapper.scrollLeft >= max - 1) {
                current = slides.length - 1;
            } else if (wrapper.scrollLeft <= 1) {
                current = 0;
            } else {
                const lefts = slideLefts();
                let best = 0;
                let bestDist = Infinity;
                lefts.forEach((left, i) => {
                    const dist = Math.abs(left - wrapper.scrollLeft);
                    if (dist < bestDist) {
                        bestDist = dist;
                        best = i;
                    }
                });
                current = best;
            }
            updateButtons();
            updateBullets(current);
        }

        let scrollSettleTimer = null;
        wrapper.addEventListener('scroll', () => {
            updateButtons();
            clearTimeout(scrollSettleTimer);
            scrollSettleTimer = setTimeout(settle, 120);
        }, { passive: true });

        // Drag / swipe with velocity-based momentum, snapping to the nearest
        // slide once it settles (matches real Swiper's default touch behavior).
        // Pointer capture is only taken once the pointer has actually moved past
        // DRAG_THRESHOLD -- capturing on every pointerdown retargets the
        // subsequent click away from the actual element under the pointer (here,
        // the slide's own <a> tag), silently breaking taps that don't drag.
        const DRAG_THRESHOLD = 8;
        let dragging = false;
        let activePointerId = null;
        let startX = 0;
        let startScroll = 0;
        let lastX = 0;
        let lastT = 0;
        let velocity = 0;
        let momentumRaf = null;

        wrapper.addEventListener('pointerdown', (e) => {
            if (e.button !== 0 && e.pointerType === 'mouse') return;
            activePointerId = e.pointerId;
            dragging = false;
            cancelAnimationFrame(momentumRaf);
            cancelAnimationFrame(animRaf);
            startX = e.pageX;
            startScroll = wrapper.scrollLeft;
            lastX = e.pageX;
            lastT = performance.now();
            velocity = 0;
        });

        wrapper.addEventListener('pointermove', (e) => {
            if (e.pointerId !== activePointerId) return;
            if (!dragging) {
                if (Math.abs(e.pageX - startX) < DRAG_THRESHOLD) return;
                dragging = true;
                wrapper.setPointerCapture(e.pointerId);
            }
            const now = performance.now();
            const dt = Math.max(1, now - lastT);
            velocity = velocity * 0.6 + ((lastX - e.pageX) / dt) * 0.4;
            lastX = e.pageX;
            lastT = now;
            wrapper.scrollLeft = startScroll - (e.pageX - startX);
        });

        function endDrag() {
            activePointerId = null;
            if (!dragging) return;
            dragging = false;
            const friction = 0.93;
            function momentum() {
                velocity *= friction;
                wrapper.scrollLeft += velocity * 16;
                if (Math.abs(velocity) > 0.3) {
                    momentumRaf = requestAnimationFrame(momentum);
                } else {
                    settle();
                }
            }
            momentumRaf = requestAnimationFrame(momentum);
        }

        wrapper.addEventListener('pointerup', endDrag);
        wrapper.addEventListener('pointercancel', endDrag);
        wrapper.addEventListener('dragstart', (e) => e.preventDefault());

        if (imageNav && prevBtn && nextBtn) {
            const imgEl = slides[0].firstElementChild;
            function updateNavPosition() {
                if (!imgEl) return;
                const swiperRect = el.getBoundingClientRect();
                const imgRect = imgEl.getBoundingClientRect();
                const top = (imgRect.top - swiperRect.top) + imgRect.height / 2;
                prevBtn.style.top = top + 'px';
                nextBtn.style.top = top + 'px';
            }
            updateNavPosition();
            if (imgEl) new ResizeObserver(updateNavPosition).observe(imgEl);
        }

        let resizeTimer = null;
        new ResizeObserver(() => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                buildBullets();
                wrapper.scrollLeft = Math.max(0, Math.min(slideLefts()[current], wrapper.scrollWidth - wrapper.clientWidth));
                updateButtons();
                updateBullets(current);
                updateControlsLock();
            }, 150);
        }).observe(wrapper);

        buildBullets();
        updateButtons();
        updateBullets(0);
        updateControlsLock();
    }

    /**
     * Single-active-slide carousel: opacity crossfade ('fade') or translateX
     * ('slide'), optional button navigation, fraction pagination ("1 / 4") and
     * autoplay. Used by .media-gallery-slideshow and the tankbar-multiblock
     * plugin's .multiblock-gallery.
     */
    function initFadeCarousel(el, opts = {}) {
        const { mode = 'fade', navigation = true, pagination = false, autoplay = false, speed = 700, draggable = true, controlsScope = el } = opts;

        const wrapper = el.querySelector('.swiper-wrapper');
        const slides = wrapper ? [...wrapper.querySelectorAll('.swiper-slide')] : [];
        if (!slides.length) return;

        let current = 0;
        slides[0].classList.add('swiper-slide-active');

        if (mode === 'slide') {
            wrapper.classList.add('carousel-mode-slide');
            wrapper.style.transitionDuration = `${speed}ms`;
        } else {
            wrapper.classList.add('carousel-mode-fade');
            requestAnimationFrame(() => {
                slides.forEach((s) => {
                    s.style.transitionDuration = `${speed}ms`;
                });
            });
        }

        function applyTransform() {
            if (mode === 'slide') {
                wrapper.style.transform = `translateX(-${current * 100}%)`;
            }
        }

        const prevBtn = navigation ? controlsScope.querySelector('.swiper-button-prev') : null;
        const nextBtn = navigation ? controlsScope.querySelector('.swiper-button-next') : null;
        const paginationEl = pagination ? controlsScope.querySelector('.swiper-pagination') : null;
        if (paginationEl) paginationEl.classList.add('swiper-pagination-fraction');

        function updatePagination() {
            if (!paginationEl) return;
            paginationEl.textContent = `${current + 1} / ${slides.length}`;
        }

        function updateButtons() {
            prevBtn?.classList.toggle('swiper-button-disabled', current <= 0);
            prevBtn?.setAttribute('aria-disabled', current <= 0 ? 'true' : 'false');
            nextBtn?.classList.toggle('swiper-button-disabled', current >= slides.length - 1);
            nextBtn?.setAttribute('aria-disabled', current >= slides.length - 1 ? 'true' : 'false');
        }

        function go(idx) {
            const next = Math.max(0, Math.min(idx, slides.length - 1));
            if (next === current) return;
            slides[current].classList.remove('swiper-slide-active');
            slides[next].classList.add('swiper-slide-active');
            current = next;
            applyTransform();
            updateButtons();
            updatePagination();
            restartAutoplay();
        }

        prevBtn?.addEventListener('click', () => go(current - 1));
        nextBtn?.addEventListener('click', () => go(current + 1));
        updateButtons();
        updatePagination();

        if (slides.length < 2) {
            prevBtn?.style.setProperty('display', 'none', 'important');
            nextBtn?.style.setProperty('display', 'none', 'important');
            if (paginationEl) paginationEl.style.setProperty('display', 'none', 'important');
        }

        if (draggable) {
            const DRAG_THRESHOLD = 8;
            let dragging = false;
            let activePointerId = null;
            let startX = 0;
            wrapper.addEventListener('pointerdown', (e) => {
                if (e.button !== 0 && e.pointerType === 'mouse') return;
                activePointerId = e.pointerId;
                dragging = false;
                startX = e.clientX;
            });
            wrapper.addEventListener('pointermove', (e) => {
                if (e.pointerId !== activePointerId || dragging) return;
                if (Math.abs(e.clientX - startX) < DRAG_THRESHOLD) return;
                dragging = true;
                wrapper.setPointerCapture(e.pointerId);
            });
            wrapper.addEventListener('pointerup', (e) => {
                activePointerId = null;
                if (!dragging) return;
                dragging = false;
                const diff = startX - e.clientX;
                if (Math.abs(diff) > 50) go(current + (diff > 0 ? 1 : -1));
            });
            wrapper.addEventListener('dragstart', (e) => e.preventDefault());
        }

        let autoplayHandle = null;
        function restartAutoplay() {
            if (!autoplay) return;
            clearInterval(autoplayHandle);
            autoplayHandle = setInterval(() => go(current + 1 >= slides.length ? 0 : current + 1), autoplay);
        }
        if (autoplay) restartAutoplay();
    }

    document.querySelectorAll('.grid-swiper').forEach((el) => {
        initGridCarousel(el, { imageNav: el.hasAttribute('data-image-nav'), speed: 700 });
    });

    document.querySelectorAll('.quote-swiper').forEach((el) => {
        initGridCarousel(el, { speed: 1000 });
    });

    document.querySelectorAll('.media-gallery-slideshow').forEach((el) => {
        const swiper = el.querySelector('.swiper');
        if (!swiper) return;
        initFadeCarousel(el, {
            mode: swiper.dataset.transitionType === 'slide' ? 'slide' : 'fade',
            speed: parseInt(swiper.dataset.transitionDuration) || 700,
            autoplay: swiper.dataset.galleryAutoplay === 'true'
                ? (parseInt(swiper.dataset.intervalDuration) || 6000)
                : false,
            navigation: swiper.dataset.galleryNavigation === 'true',
            pagination: swiper.dataset.galleryPagination === 'true',
        });
    });

    document.querySelectorAll('.multiblock-gallery').forEach((el) => {
        const swiper = el.querySelector('.swiper');
        if (!swiper) return;
        const galleryButtons = swiper.dataset.galleryButtons === 'true';
        // The pagination/nav markup (.multiblock-gallery-controls) is a sibling of
        // .multiblock-gallery at the block root, not nested inside it -- see
        // tankbar-multiblock's render.php.
        const controlsScope = el.closest('.wp-block-create-block-tankbar-multiblock') || el;
        initFadeCarousel(el, {
            mode: swiper.dataset.transitionStyle === 'slide' ? 'slide' : 'fade',
            speed: parseInt(swiper.dataset.transitionDuration) || 1500,
            autoplay: swiper.dataset.galleryAutoplay === 'true'
                ? (parseInt(swiper.dataset.intervalDuration) || 6000)
                : false,
            navigation: galleryButtons,
            pagination: swiper.dataset.galleryPagination === 'true',
            draggable: galleryButtons,
            controlsScope,
        });
    });
});

//# sourceMappingURL=swiper.js.map
